import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMe, portalDashboard, STAGE_LABEL, type MeResponse, type PortalEngagementRow } from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Motif } from '../components/ui/Motif';
import { celebrateOnce } from '../components/ui/confetti';

/** Phase 3 · the labor company's home — every package it's invited to + bid status. */
export default function PortalDashboard() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const { data, isLoading } = useQuery<{ engagements: PortalEngagementRow[] }>({
    queryKey: ['portal-dashboard'], queryFn: portalDashboard, enabled: me?.account?.role === 'labor_co',
  });

  const rows = data?.engagements ?? [];
  // 中标 deserves a moment — confetti once per engagement (localStorage-guarded), never on re-visits.
  useEffect(() => {
    rows.forEach((e) => { if (e.awarded_engagement_id === e.id) celebrateOnce(`award_${e.id}`); });
  }, [rows]);

  if (!me?.authenticated) return <div className="card">请先登录 / please log in.</div>;
  if (me.account?.role !== 'labor_co') return <div className="card">此页仅劳务公司可见 / labor-company only.</div>;

  return (
    <div className="portal">
      <h2 style={{ marginBottom: 2 }}>
        {me.account.contact_name ? `${me.account.contact_name}（${me.account.display_name}）` : me.account.display_name}，欢迎回来！ / Welcome back
      </h2>
      <p className="muted small" style={{ marginTop: 2 }}>这是您的投标台 — 受邀项目和报价进展都在这里。 / Your bidding desk — invitations and progress live here.</p>
      {me.wecom_outbound_configured === false && (
        <div className="pill pill-warn" style={{ marginBottom: 8, display: 'inline-block' }}>WeCom 未配置 — 站内通知为开发态 / dev-mode messaging</div>
      )}
      {isLoading && (
        <>
          <div className="card"><Skeleton lines={2} /></div>
          <div className="card"><Skeleton lines={2} /></div>
        </>
      )}
      {rows.length === 0 && !isLoading && (
        <EmptyState
          motif="wait"
          title="还没有新邀请 / No invitations yet"
          hint="一有项目找您，这里马上就能看到。 / New projects will show up here first."
        />
      )}
      {rows.map((e) => {
        const awarded = e.awarded_engagement_id === e.id;
        const lost = !!e.awarded_engagement_id && !awarded;
        return (
          <div key={e.id} className={`card eng-card${awarded ? ' awarded' : lost ? ' lost' : ''}`} style={{ marginBottom: 10 }}>
            <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
              {awarded && <Motif variant="cheer" size={30} />}
              <strong>{e.package_name}</strong>
              <span className="pill pill-stage small">{STAGE_LABEL[e.current_stage] ?? `阶段 ${e.current_stage} / stage ${e.current_stage}`}</span>
              {awarded && <span className="pill pill-good small">🎉 恭喜中标！ / You won this one!</span>}
              {lost && <span className="pill pill-muted small">未中标 / not selected</span>}
              <span className="muted small">{e.bid_rounds > 0 ? `已报 ${e.bid_rounds} 轮${e.last_round ? ` · 最新第 ${e.last_round} 轮` : ''} / ${e.bid_rounds} round(s) in` : '待报价 / awaiting your bid'}</span>
              <span className="spacer" />
              <Link to={`/portal/engagements/${e.id}`}><button>进入 / Open →</button></Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}
